// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useState } from "react";
import {
  ContentState,
  DraftInlineStyle,
  EditorState,
  Modifier
} from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "@shelterzoom/draft-js-import-html";
import { ContentBlock, Editor } from "react-draft-wysiwyg";

import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { TemplateInputTextPropertiesDto } from "../../utils/htmlTemplate/dto/template.dto";
import { Alert } from "antd";
import { useTranslation } from "react-i18next";

import { RichUtils } from "draft-js";
import { Options as ExportOptions } from "draft-js-export-html";
import { RenderConfig } from "draft-js-export-html";
import htmlToDraft from "html-to-draftjs";

const FOCUS_KEY = "FOCUS";
const FOCUS_CLASS_NAME = "newsThemeFocus";
const LINE_KEY = "LINE";
const LINE_CLASS_NAME = "newsThemeLine";
const QUOTE_KEY = "QUOTE";
const QUOTE_CLASS_NAME = "newsThemeQuote";

const importOptions: any = {
  customInlineFn: (element: any) => {
    const style = [];
    if (element.className === FOCUS_CLASS_NAME) {
      style.push(FOCUS_KEY);
    } else if (element.className === QUOTE_CLASS_NAME) {
      style.push(QUOTE_KEY);
    }
    if (element.style["text-align"]) {
      style.push("textalign-" + element.style["text-align"]);
    }
    if (style.length > 0) {
      return {
        type: "STYLE",
        style
      };
    }
  },
  customBlockFn: (element: any) => {
    if (element.style["text-align"]) {
      return {
        data: {
          "text-align": element.style["text-align"]
        }
      };
    }
  }
};

// TODO retirer la mention inlineStyleFn si le projet draft-js-export-html corrige la définition de type
const exportOptions:
  | ExportOptions
  | {
      inlineStyleFn?: (style: DraftInlineStyle) => RenderConfig | undefined;
    } = {
  defaultBlockTag: "div",
  inlineStyleFn: (styles: any) => {
    const focus = styles
      .filter((style: string) => style === FOCUS_KEY || style === QUOTE_KEY)
      .first();
    if (focus) {
      return {
        element: "span",
        attributes: {
          class: focus === FOCUS_KEY ? FOCUS_CLASS_NAME : QUOTE_CLASS_NAME
        }
      };
    }
  },
  blockStyleFn: (block: ContentBlock): RenderConfig | undefined => {
    const alignment = block.getData().get("text-align");
    if (typeof alignment === "string") {
      return {
        style: {
          textAlign: alignment
        }
      };
    }
    return undefined;
  }
};

type ToolbarButtonProps = {
  editorState: EditorState;
  onChange?: (editorState: EditorState) => unknown;
};

const FocusToolbarButton: React.FunctionComponent<ToolbarButtonProps> = ({
  editorState,
  onChange
}: ToolbarButtonProps) => {
  const toggleBold = (): void => {
    const newState = RichUtils.toggleInlineStyle(editorState, FOCUS_KEY);
    if (newState && !!onChange) {
      onChange(newState);
    }
  };

  const { t } = useTranslation();
  const className = `rdw-option-wrapper ${FOCUS_CLASS_NAME}`;
  return (
    <strong className={className} onClick={toggleBold}>
      {t("edit_news.editor.focus")}
    </strong>
  );
};

const LineToolbarButton: React.FunctionComponent<ToolbarButtonProps> = ({
  editorState,
  onChange
}: ToolbarButtonProps) => {
  const addLine = (): void => {
    const line =
      "<p></p><p style='text-align:center;'>_______________</p><p></p>";
    const { contentBlocks, entityMap } = htmlToDraft(line);
    const contentState = Modifier.replaceWithFragment(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      ContentState.createFromBlockArray(contentBlocks, entityMap).getBlockMap()
    );
    if (onChange) {
      onChange(
        EditorState.push(editorState, contentState, "insert-characters")
      );
    }
  };

  const { t } = useTranslation();
  const className = `rdw-option-wrapper ${LINE_CLASS_NAME}`;
  return (
    <div className={className} onClick={addLine}>
      {t("edit_news.editor.line")}
    </div>
  );
};

const QuoteToolbarButton: React.FunctionComponent<ToolbarButtonProps> = ({
  editorState,
  onChange
}) => {
  const toggleStyle = (): void => {
    const newState = RichUtils.toggleInlineStyle(editorState, QUOTE_KEY);
    if (newState && !!onChange) {
      onChange(newState);
    }
  };

  const { t } = useTranslation();
  const className = `rdw-option-wrapper ${QUOTE_CLASS_NAME}`;
  return (
    <div className={className} onClick={toggleStyle}>
      {t("edit_news.editor.quote")}
    </div>
  );
};

export const HtmlEditor: React.FunctionComponent<{
  debug?: boolean;
  text?: string;
  inputIndex: number;
  handleTextContentChange: (htmlContent: string, inputIndex: number) => void;
  editMode?: boolean;
  inputProperties?: TemplateInputTextPropertiesDto;
}> = ({
  debug = false,
  text = undefined,
  inputIndex,
  handleTextContentChange,
  editMode = true,
  inputProperties
}: {
  debug?: boolean;
  text?: string;
  inputIndex: number;
  handleTextContentChange: (htmlContent: string, inputIndex: number) => void;
  editMode?: boolean;
  inputProperties?: TemplateInputTextPropertiesDto;
}) => {
  const { t } = useTranslation();
  const [editorState, setEditorState] = useState<EditorState>(
    text
      ? EditorState.createWithContent(stateFromHTML(text, importOptions))
      : EditorState.createEmpty()
  );

  const [html, setHtml] = useState<string | null>(
    text ? text.replace(/^<p><br><\/p>$/, "") : null
  );

  const handleOnChange = (editorState: EditorState): void => {
    setEditorState(editorState);
    const htmlState = stateToHTML(
      editorState.getCurrentContent(),
      exportOptions as ExportOptions
    );
    const parsedState: string | null = htmlState
      ? htmlState.replace(/^<p><br><\/p>$/, "")
      : null;
    setHtml(parsedState);
    const contentState = parsedState ? parsedState : "";
    handleTextContentChange(contentState, inputIndex);
  };

  const toolbar = {
    options: [
      "inline",
      "blockType",
      "list",
      "textAlign",
      "link",
      "remove",
      "history"
    ],
    inline: {
      inDropdown: false,
      className: undefined,
      options: ["bold", "italic", "underline", "strikethrough"],
      bold: { /*icon: bold, */ className: undefined },
      italic: { /*icon: italic, */ className: undefined },
      underline: { /*icon: underline, */ className: undefined },
      strikethrough: { /*icon: strikethrough, */ className: undefined },
      monospace: { /*icon: monospace, */ className: undefined },
      superscript: { /*icon: superscript, */ className: undefined },
      subscript: { /*icon: subscript, */ className: undefined }
    },
    blockType: {
      inDropdown: true,
      options: ["Normal", "H1", "H2", "H3", "H4", "H5", "H6"],
      className: undefined,
      dropdownClassName: undefined
    },
    list: {
      inDropdown: false,
      className: undefined,
      options: ["unordered", "ordered" /*, "indent", "outdent"*/],
      unordered: { /*icon: unordered, */ className: undefined },
      ordered: { /*icon: ordered, */ className: undefined },
      indent: { /*icon: indent, */ className: undefined },
      outdent: { /*icon: outdent, */ className: undefined }
    },
    textAlign: {
      inDropdown: false,
      className: undefined,
      options: ["left", "center", "right", "justify"],
      left: { /*icon: left, */ className: undefined },
      center: { /*icon: center, */ className: undefined },
      right: { /*icon: right, */ className: undefined },
      justify: { /*icon: justify, */ className: undefined }
    },
    link: {
      inDropdown: false,
      className: undefined,
      popClassName: undefined,
      options: ["link", "unlink"],
      link: { /*icon: link, */ className: undefined },
      unlink: { /*icon: unlink, */ className: undefined }
    },
    remove: { /*icon: eraser, */ className: undefined },
    history: {
      inDropdown: false,
      className: undefined,
      options: ["undo", "redo"],
      undo: { /*icon: undo, */ className: undefined },
      redo: { /*icon: redo, */ className: undefined }
    }
  };

  return (
    <>
      <div className="position-relative pt-1">
        <div>
          <Editor
            editorState={editorState}
            toolbarClassName="cncc-richtext-toolbar"
            wrapperClassName={`cncc-richtext-wrapper${
              !editMode ? " cncc-richtext-wrapper-readonly" : ""
            }`}
            editorClassName="cncc-richtext-editor"
            stripPastedStyles={true}
            spellCheck={true}
            toolbar={toolbar}
            toolbarCustomButtons={[
              <FocusToolbarButton
                key="FocusToolbarButton"
                editorState={editorState}
              />,
              <LineToolbarButton
                key="LineToolbarButton"
                editorState={editorState}
              />,
              <QuoteToolbarButton
                key="LineToolbarButton"
                editorState={editorState}
              />
            ]}
            customStyleMap={{
              [FOCUS_KEY]: {
                fontWeight: "bold",
                color: "#F37021"
              },
              [LINE_KEY]: {
                textAlign: "center"
              },
              [QUOTE_KEY]: {
                fontWeight: "bold",
                fontStyle: "italic",
                color: "#3D6FB6"
              }
            }}
            onEditorStateChange={handleOnChange}
            readOnly={!editMode}
            wrapperStyle={
              !!inputProperties?.maxSize &&
              (html?.length || 0) > inputProperties?.maxSize
                ? { border: "solid red" }
                : {}
            }
          />
        </div>

        {!!inputProperties?.maxSize &&
        (html?.length || 0) > inputProperties?.maxSize ? (
          <Alert
            message={t("global.error.rich_text_too_long", {
              currentLength: html?.length,
              maxLength: inputProperties?.maxSize
            })}
            type="error"
          />
        ) : (
          <></>
        )}

        {debug ? (
          <div>
            <h2 className="preview">Code HTML</h2>
            <div>{html}</div>
          </div>
        ) : (
          ""
        )}
      </div>
    </>
  );
};
